import type { CaptureStatus } from '../../data/database.types';

/**
 * The locked Capture status vocabulary (PT §6 and the design export).
 *
 * **GLYPH + WORD, NEVER COLOUR ALONE** — the design export states that rule
 * explicitly, and it is an accessibility requirement rather than a style: on a
 * bright site, through a screen protector, with dusty hands, colour alone is
 * not readable.
 *
 * Exactly three. **REPAIRED must never be treated as CLOSED**: one is
 * completion, the other is ending without it, and conflating them destroys the
 * meaning of both. **"Needs Review" is not here** — it is an AI confidence
 * badge that renders on top of a Capture, never a fourth state (PT §6, §10).
 */
export const CAPTURE_STATUS_LABEL: Record<CaptureStatus, string> = {
  open: '● OPEN',
  repaired: '✓ REPAIRED',
  closed: '⊘ CLOSED',
};

/** Creation order, with OPEN first because it is the default and the common case. */
export const CAPTURE_STATUS_ORDER: CaptureStatus[] = ['open', 'repaired', 'closed'];

/**
 * `CAP-0042` from the stored integer.
 *
 * **The database stores the number; the padding is presentation.** Formatting
 * here rather than storing the string is what keeps a display decision out of
 * the data — and it is why nothing client-side ever invents one: the integer
 * always comes from the server (INV-T06).
 *
 * Four digits is the design export's format. A Project that somehow exceeds
 * 9999 Captures simply renders more digits rather than truncating, because a
 * wrong Capture ID is worse than a wide one.
 */
export function formatCaptureNumber(captureNumber: number): string {
  return `CAP-${String(captureNumber).padStart(4, '0')}`;
}

/** Shown whenever no location has been accepted. NEVER a guess (PT §5). */
export const NO_LOCATION_LABEL = 'Location not set';

/**
 * The Capture title line: `CAP-0042 — Unit 1207 Living Room`.
 *
 * **The location is ACCEPTED organization, or nothing** (M7 / S07-03). Until
 * S07-02 there was no location to show and this returned the fallback
 * unconditionally; it now takes one, and the fallback survives for every
 * Capture whose location is genuinely unknown — one that was never organized,
 * one the model could not place, or one Brandon deliberately cleared.
 *
 * **A location is never invented** (PT §5). There is no branch here that
 * derives, abbreviates or guesses a place: it prints what was accepted, or it
 * prints that nothing was.
 *
 * ONE FORMATTER, used by the list and by Detail, so the two can never drift
 * into describing the same Capture differently.
 */
export function formatCaptureTitle(captureNumber: number, locationDisplay?: string | null): string {
  const location = locationDisplay?.trim();
  return `${formatCaptureNumber(captureNumber)} — ${
    location && location.length > 0 ? location : NO_LOCATION_LABEL
  }`;
}

/**
 * Whitespace the SERVER treats as blank (migration 0005, finding F4).
 *
 * **This is a mirror, and mirrors drift**, so it is worth saying exactly what
 * it is for: the server is the authority and decides whether a transcript
 * version row is written. This copy exists only so the Save control can be
 * disabled before a pointless round trip. If the two ever disagree, the server
 * wins and the only symptom is a Save button enabled a moment too early.
 *
 * **U+200B and the zero-width joiners are deliberately absent**, exactly as on
 * the server: they are formatting controls that carry meaning inside words and
 * emoji sequences, not whitespace.
 */
const SERVER_BLANK = /^[\s\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000\u2028\u2029\uFEFF]*$/;

/**
 * True when the server would store no transcript version for this text.
 *
 * Written with ESCAPES, not literal characters: U+2028 and U+2029 are line
 * terminators in JavaScript source, so a literal one inside a regex literal
 * ends the expression mid-class. It compiled to a syntax error rather than a
 * subtly wrong pattern, which is the good version of that mistake.
 *
 * The class is spelled out even though JavaScript's own `\s` already covers
 * every one of these. `\s` and PostgreSQL's `[[:space:]]` are NOT the same set
 * — that difference is the entire reason finding F4 existed — so listing the
 * characters keeps this readable as the server's set rather than as whatever
 * the JavaScript engine happens to mean by whitespace today.
 */
export function isBlankTranscript(value: string): boolean {
  return SERVER_BLANK.test(value);
}
